import React, { useState, useCallback, useRef, useEffect, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useAlert } from '../../../context/alert';
import { useUserContext } from '../../../context/user';
import { UserSuggestionResponse } from '../../../types/api/userSuggestions';
import { updateUserSuggestionsStatus } from '../../../services/userSuggestions';
import { FeedbackStatus, FeedbackStatusUpdateRequest } from '../../../types/api/feedback';
import ModalSheet from '../../basic/ModalSheet';
import { UserHeaderCard } from '../../basic/UserHeaderCard';
import { FeedbackStatusButton } from '../../basic/AppealStatusButton';
import { Avatar, Stack, Textarea, FormControl, Button, Text, Label } from '@primer/react'
import { ChevronRightIcon } from '@primer/octicons-react';
import { formatDateTime } from '../../../helpers/dates';

interface DisplayUserSuggestionModalProps {
    suggestion: UserSuggestionResponse
    isOpen: boolean;
    onDidSuggestionStatusChanged: () => void;
    onClose: () => void;
}

export const DisplayUserSuggestionModal: React.FC<DisplayUserSuggestionModalProps> = ({ suggestion, isOpen, onClose, onDidSuggestionStatusChanged }) => {

    const { profile } = useUserContext()
    const navigate = useNavigate();
    const [loadingStatus, setLoadingStatus] = useState(false);
    const [suggestionStatus, setSuggestionStatus] = useState<FeedbackStatus>(FeedbackStatus.NEW);

    useEffect(() => {
        setSuggestionStatus(suggestion.status)
    }, [suggestion]);

    const updateStatus = async (newStatus: FeedbackStatus) => {
        setLoadingStatus(true);

        setSuggestionStatus(newStatus)
        const data: FeedbackStatusUpdateRequest = {
            ids: [suggestion.id],
            status: newStatus
        }

        try {
            await updateUserSuggestionsStatus(data);
            onDidSuggestionStatusChanged();
        } catch (error) {
        } finally {
            setLoadingStatus(false);
        }
    };

    return (
        <ModalSheet
            isOpen={isOpen}
            onClose={onClose}
            title="Предложение пользователя"
            size="middle"
            headerSize="regular"
        >
            {({ setTitle }) => (
                <Stack className='mt-[16px]'>
                    <Stack direction="horizontal" align="center" justify="space-between" gap="condensed">
                        <FeedbackStatusButton
                            isDisabled={loadingStatus}
                            currentStatus={suggestionStatus}
                            onStatusChange={updateStatus}
                        />
                        <Text className='text-[14px] text-aloix-grey break-words'>
                            {formatDateTime(suggestion.createdAt, ',')}
                        </Text>
                    </Stack>
                    <div className="bg-header border border-border rounded-lg w-full mb-2">
                        <div className="bg-white w-full rounded-t-lg border-b">
                            <div className="p-4">
                                <UserHeaderCard
                                    user={suggestion.user}
                                    isLarge={false}
                                    isSupport={false}
                                    onClick={() => navigate('/users/user?id=' + suggestion.user.id)} />
                            </div>
                        </div>

                        <div className="bg-header w-full rounded-b-lg border-b">
                            <div className="ml-[14px] mr-[14px] mb-4">
                                <Stack direction="horizontal" justify="space-between" gap="condensed">
                                    <Stack direction="horizontal" align="center" gap="condensed">
                                        <Text className='text-[14px] text-aloix-grey ml-1 mt-3 break-words'>
                                            {suggestion.text}
                                        </Text>
                                    </Stack>

                                </Stack>
                            </div>
                        </div>
                    </div>

                    {
                        suggestion.appScreen && (
                            <div className="bg-header w-full rounded-lg border">
                                <div className="mt-4 ml-[14px] mr-[14px] mb-4">
                                    <Stack direction="horizontal" justify="space-between" gap="condensed">
                                        <Stack direction="horizontal" align="center" gap="condensed">
                                            <Text className='text-[14px] text-aloix-grey ml-1 mt-3'>
                                                {suggestion.appScreen}
                                            </Text>
                                        </Stack>

                                    </Stack>
                                </div>
                            </div>
                        )
                    }
                </Stack>
            )
            }
        </ModalSheet >
    );
};